import { formatTicker, ThemeCompany } from '@api';
import { PillDropdown, SkeletonLoader } from '@design';
import { useThemeStore } from '@terminal/components/theme-controller/stores/useThemeStore';
import { isDarkTheme } from '@terminal/components/theme-controller/utils/theme-utils/theme-utils';
import { TooltipContainer } from '@terminal/components/tooltip-container/TooltipContainer';
import { TruncateTooltip } from '@terminal/components/truncate-tooltip/TruncateTooltip';
import { useEntity } from '@terminal/react-query-hooks/entities/useEntities';
import React, { CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './CompanyItem.module.scss';

const MAX_NAME_LENGTH = 30;

type LogoSizeStyle = CSSProperties & { '--logo-size': string };

type CompanyItemProps = {
  company: ThemeCompany;
  onPillClick: (entityTag: string) => void;
  iconSize?: number;
};

export const CompanyItem = ({
  company,
  onPillClick,
  iconSize = 20,
}: CompanyItemProps) => {
  const { t } = useTranslation(['widget']);
  const themeName = useThemeStore(state => state.themeName);
  const isDark = isDarkTheme(themeName);
  const [imageError, setImageError] = useState(false);
  const { company: tag, relationship } = company;

  const { isFetching, isError, error, data } = useEntity(tag);

  if (isError) {
    throw new Error(error.message);
  }

  if (isFetching || !data?.data) {
    return (
      <SkeletonLoader
        areas={[
          { styles: { width: '125px', height: '48px', borderRadius: '8px' } },
        ]}
      />
    );
  }

  const { logo_urls, name } = data.data;
  const logoUrl = isDark ? logo_urls.light_png : logo_urls.dark_png;
  const logoSizeStyle: LogoSizeStyle = { '--logo-size': `${iconSize}px` };

  return (
    <TooltipContainer
      label={t('widget:themeOverview.rank', {
        rank: relationship.rank,
      })}
      placement="top-start"
    >
      <div className={styles.pillContainer}>
        <PillDropdown
          label={
            <span className={styles.styledHeader}>
              {!!logoUrl && !imageError ? (
                <img
                  className={styles.styledLogoImg}
                  src={logoUrl}
                  onError={() => setImageError(true)}
                  style={logoSizeStyle}
                />
              ) : (
                <div className={styles.logoFallback} style={logoSizeStyle}>
                  {name.charAt(0)}
                </div>
              )}
              <div className={styles.assetHeader} data-asset-header>
                <TruncateTooltip
                  placement="top"
                  data-testid="company-label"
                  id={`company-label`}
                  text={formatTicker(data.data)}
                  atLength={MAX_NAME_LENGTH}
                />
                <TruncateTooltip
                  placement="bottom"
                  data-testid="company-name"
                  id={`company-name`}
                  text={name}
                  atLength={MAX_NAME_LENGTH}
                />
              </div>
            </span>
          }
          value={tag}
          onClickPill={onPillClick}
        />
      </div>
    </TooltipContainer>
  );
};
